import { useEffect, useState } from "react";
import { Download, X } from "lucide-react";

/**
 * PWAInstallButton — surfaces Chromium's `beforeinstallprompt` as a
 * Grubpe-styled CTA. iOS Safari users see a fallback hint instead
 * (since iOS doesn't expose the install prompt API).
 */
export default function PWAInstallButton({ variant = "pill" }) {
  const [deferred, setDeferred] = useState(null);
  const [installed, setInstalled] = useState(false);
  const [iosHint, setIosHint] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem("grubpe_install_dismissed") === "1"; } catch { return false; }
  });

  useEffect(() => {
    const ua = window.navigator.userAgent || "";
    const isStandalone = window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone;
    if (isStandalone) { setInstalled(true); return; }

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    const onInstalled = () => { setInstalled(true); setDeferred(null); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);

    // iOS Safari: show a hint since beforeinstallprompt never fires.
    const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
    if (isIOS && isSafari) setIosHint(true);

    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (installed || dismissed) return null;
  if (!deferred && !iosHint) return null;

  const dismiss = () => {
    setDismissed(true);
    try { localStorage.setItem("grubpe_install_dismissed", "1"); } catch {}
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    const choice = await deferred.userChoice;
    if (choice?.outcome === "accepted") setInstalled(true);
    setDeferred(null);
  };

  if (variant === "banner") {
    return (
      <div data-testid="pwa-install-banner" className="bg-ink text-white rounded-2xl p-4 flex items-start gap-3 shadow-soft">
        <div className="w-10 h-10 rounded-full bg-sun text-ink flex items-center justify-center flex-shrink-0">
          <Download size={18} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-heading font-bold text-base">Install Grubpe</div>
          <div className="text-xs text-white/70 mt-0.5">
            {iosHint ? "Tap Share, then 'Add to Home Screen' for an app-like experience." : "Add to your home screen for one-tap ordering."}
          </div>
          {!iosHint && (
            <button data-testid="pwa-install-cta" onClick={install} className="mt-2 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider bg-sun text-ink px-3 py-1.5 rounded-full">
              Install <Download size={12} />
            </button>
          )}
        </div>
        <button data-testid="pwa-install-dismiss" onClick={dismiss} className="text-white/50 hover:text-white"><X size={16} /></button>
      </div>
    );
  }

  return (
    <button
      data-testid="pwa-install-pill"
      onClick={iosHint ? dismiss : install}
      className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider bg-ink text-white px-3 py-1.5 rounded-full hover:bg-brand transition-colors"
      title={iosHint ? "Use Safari's Share menu to add to home screen" : "Install Grubpe app"}
    >
      <Download size={12} /> {iosHint ? "Add to Home Screen" : "Install app"}
    </button>
  );
}
